// App.js - Version avec navigation sticky via wrapper personnalisé
import React, { useEffect, useRef, useContext } from 'react';
import { NavigationContainer } from '@react-navigation/native';
import { createStackNavigator } from '@react-navigation/stack';
import { StatusBar } from 'expo-status-bar';
import { SafeAreaProvider } from 'react-native-safe-area-context';
import { BackHandler, Platform, Alert } from 'react-native';

// Import Google Fonts
import { 
  useFonts,
  Montserrat_400Regular,
  Montserrat_500Medium,
  Montserrat_600SemiBold,
  Montserrat_700Bold
} from '@expo-google-fonts/montserrat';

// Import screens
import LoginScreen from './screens/LoginScreen';
import HomeScreen from './screens/HomeScreen';
import SelectionScreen from './screens/SelectionScreen';
import ScoringScreen from './screens/ScoringScreen';
import SettingsScreen from './screens/SettingsScreen';
import HelpScreen from './screens/HelpScreen';

// Import contexts
import { AuthProvider, AuthContext } from './contexts/AuthContext';
import { SheetProvider } from './contexts/SheetContext';
import { NavigationProvider, NavigationContext } from './contexts/NavigationContext';
import hapticService from './services/hapticService';

// Import components
import LoadingScreen from './components/LoadingScreen';
import StickyHeaderWrapper from './components/StickyHeaderWrapper';

// Import services
import storageMigrationService from './services/storageMigration';
import debugService, { enableGlobalDebugging } from './services/debugService';

// Import configuration
import { config } from './config';

const Stack = createStackNavigator();

// Configuration avec hash routing
const linking = Platform.OS === 'web' ? {
  prefixes: [window.location.origin],
  config: {
    screens: {
      Login: '',
      Home: 'home',
      Selection: 'selection/:competitionName',
      Scoring: 'scoring/:competitionName/:riderName',
      Settings: 'settings',
      Help: 'help',
    },
  },
  getInitialURL: () => {
    if (!window.location.hash && window.location.pathname !== '/') {
      const newUrl = window.location.origin + '/#' + window.location.pathname + window.location.search;
      window.history.replaceState(null, '', newUrl);
      return newUrl;
    }
    
    if (!window.location.hash) {
      window.history.replaceState(null, '', window.location.origin + '/#/');
    }
    
    return window.location.href;
  },
  subscribe: (listener) => {
    const onHashChange = () => listener(window.location.href);
    window.addEventListener('hashchange', onHashChange);
    return () => window.removeEventListener('hashchange', onHashChange);
  },
} : undefined;

// 🆕 WRAPPERS AVEC STICKY HEADER
const LoginScreenWithHeader = (props) => (
  <StickyHeaderWrapper title="Trials Live Scoring - Login" showBackButton={false}>
    <LoginScreen {...props} />
  </StickyHeaderWrapper>
);

const HomeScreenWithHeader = (props) => (
  <StickyHeaderWrapper 
    title="Trials Live Scoring" 
    showBackButton={false}
    titleStyle={{ marginLeft: 0 }} // 🆕 Enlever le margin-left
    rightButtons={[
      { icon: 'help-circle-outline', onPress: () => props.navigation.navigate('Help') },
      { icon: 'settings-outline', onPress: () => props.navigation.navigate('Settings') }
    ]}
  >
    <HomeScreen {...props} />
  </StickyHeaderWrapper>
);

const SelectionScreenWithHeader = (props) => (
  <StickyHeaderWrapper title={props.route?.params?.competitionName || 'Select Rider'}>
    <SelectionScreen {...props} />
  </StickyHeaderWrapper>
);

const SettingsScreenWithHeader = (props) => (
  <StickyHeaderWrapper title="Settings">
    <SettingsScreen {...props} />
  </StickyHeaderWrapper>
);

const HelpScreenWithHeader = (props) => (
  <StickyHeaderWrapper title="Help">
    <HelpScreen {...props} />
  </StickyHeaderWrapper>
);

// 🆕 WRAPPER CONDITIONNEL POUR SCORINGSCREEN
const ScoringScreenWithHeader = (props) => {
  if (config.ENABLE_STICKY_SCORING_HEADER) {
    return (
      <StickyHeaderWrapper title={props.route?.params?.competitionName || 'Scoring'}>
        <ScoringScreen {...props} />
      </StickyHeaderWrapper>
    );
  }
  return <ScoringScreen {...props} />;
};

function AppNavigator() {
  const navigationRef = useRef();
  
  let getTransitionOptions, navLoading = false;
  let user, authLoading = false;
  
  try {
    const navContext = useContext(NavigationContext);
    getTransitionOptions = navContext?.getTransitionOptions;
    navLoading = navContext?.isLoading || false;
  } catch (e) {
    getTransitionOptions = () => ({
      gestureEnabled: true,
      gestureDirection: 'horizontal',
      cardStyleInterpolator: ({ current, layouts }) => ({
        cardStyle: {
          transform: [{
            translateX: current.progress.interpolate({
              inputRange: [0, 1],
              outputRange: [layouts.screen.width, 0],
              extrapolate: 'clamp',
            }),
          }],
        },
      }),
    });
  }
  
  try {
    const authContext = useContext(AuthContext);
    user = authContext?.user;
    authLoading = authContext?.isLoading || false;
  } catch (e) {
    console.log('AuthContext error:', e);
  }

  // Gestion du bouton retour Android
  useEffect(() => {
    if (Platform.OS === 'android') {
      const backAction = () => {
        const currentRoute = navigationRef.current?.getCurrentRoute()?.name;
        
        if (currentRoute === 'Login' || currentRoute === 'Home') {
          Alert.alert(
            'Exit App',
            'Are you sure you want to exit?',
            [
              { text: 'Cancel', style: 'cancel' },
              { text: 'Yes', onPress: () => BackHandler.exitApp() }
            ],
            { cancelable: true }
          );
          return true;
        }
        
        return false;
      };
      
      const backHandler = BackHandler.addEventListener('hardwareBackPress', backAction);
      return () => backHandler.remove();
    }
  }, []);

  if (authLoading || navLoading) {
    return <LoadingScreen message="Initializing app..." variant="fullscreen" />;
  }

  return (
    <NavigationContainer
      ref={navigationRef}
      linking={linking}
      onStateChange={() => {
        try {
          hapticService.navigation();
        } catch (e) {
          console.log('Haptic error:', e);
        }
      }}
    >
      <StatusBar style="light" translucent />
      <Stack.Navigator
        initialRouteName={user ? 'Home' : 'Login'}
        screenOptions={({ route }) => ({
          // Headers désactivés si sticky activé, sinon headers normaux
          headerShown: route.name === 'Scoring' ? !config.ENABLE_STICKY_SCORING_HEADER : !config.ENABLE_STICKY_HEADER,
          headerStyle: {
            backgroundColor: '#0066cc',
            elevation: 0,
            shadowOpacity: 0,
            borderBottomWidth: 0,
          },
          headerTintColor: '#fff',
          headerTitleStyle: {
            
            fontSize: 20,
            fontFamily: 'Montserrat_600SemiBold',
          },
          headerBackTitleVisible: false,
          headerTranslucent: false,
          cardStyle: { backgroundColor: '#f5f5f5' },
          cardOverlayEnabled: false,
          animationEnabled: true,
          animationTypeForReplace: 'push',
          
          // Transitions
          ...(route.name === 'Login' || route.name === 'Home' 
            ? {
                cardStyleInterpolator: () => ({
                  cardStyle: { opacity: 1 },
                }),
                transitionSpec: {
                  open: { animation: 'timing', config: { duration: 0, useNativeDriver: true } },
                  close: { animation: 'timing', config: { duration: 0, useNativeDriver: true } },
                },
                gestureEnabled: false,
              }
            : getTransitionOptions())
        })}
      >
        <Stack.Screen name="Login" component={LoginScreenWithHeader} />
        <Stack.Screen name="Home" component={HomeScreenWithHeader} />
        <Stack.Screen name="Selection" component={SelectionScreenWithHeader} />
        <Stack.Screen name="Scoring" component={ScoringScreenWithHeader} />
        <Stack.Screen name="Settings" component={SettingsScreenWithHeader} />
        <Stack.Screen name="Help" component={HelpScreenWithHeader} />
      </Stack.Navigator>
    </NavigationContainer>
  );
}

export default function App() {
  let [fontsLoaded] = useFonts({
    Montserrat_400Regular,
    Montserrat_500Medium,
    Montserrat_600SemiBold,
    Montserrat_700Bold
  });

  // Initialisation de l'app
  useEffect(() => {
    const initializeApp = async () => {
      console.log('🚀 Initializing Trials Live Scoring...');
      
      try {
        if (__DEV__ || process.env.NODE_ENV === 'development') {
          enableGlobalDebugging();
        }
        
        const migrationCompleted = await storageMigrationService.checkMigrationStatus();
        
        if (!migrationCompleted) {
          console.log('🔄 Starting migration...');
          await storageMigrationService.migrateStorageData();
        }
        
        console.log('🎉 App initialization completed');
        console.log('🔧 Sticky header enabled:', config.ENABLE_STICKY_HEADER);
        
      } catch (error) {
        console.error('❌ App initialization failed:', error);
      }
    };
    
    const timer = setTimeout(initializeApp, 100);
    return () => clearTimeout(timer);
  }, []);

  if (!fontsLoaded) {
    return <LoadingScreen message="Loading fonts..." variant="fullscreen" />;
  }

  return (
    <SafeAreaProvider>
      <NavigationProvider>
        <AuthProvider>
          <SheetProvider>
            <AppNavigator />
          </SheetProvider>
        </AuthProvider>
      </NavigationProvider>
    </SafeAreaProvider>
  );
}